import React from "react";
import { ChevronRight, Home, Building2, Folder } from "lucide-react";
import { cn } from "@/lib/utils";

interface BreadcrumbItem {
  label: string;
  icon?: React.ReactNode;
  onClick?: () => void;
  isActive?: boolean;
}

interface BreadcrumbProps {
  items: BreadcrumbItem[];
  className?: string;
}

export const Breadcrumb: React.FC<BreadcrumbProps> = ({ items, className }) => {
  return (
    <nav 
      className={cn("flex items-center space-x-1 text-sm", className)}
      aria-label="Breadcrumb"
    >
      {items.map((item, index) => (
        <React.Fragment key={index}>
          {index > 0 && (
            <ChevronRight className="h-4 w-4 text-slate-400 flex-shrink-0" />
          )}
          <div className="flex items-center">
            {item.onClick ? (
              <button
                onClick={item.onClick}
                className={cn(
                  "flex items-center gap-1.5 px-2 py-1 rounded-md transition-colors hover:bg-slate-100",
                  item.isActive 
                    ? "text-primary font-medium bg-primary/5" 
                    : "text-slate-600 hover:text-slate-900"
                )}
              >
                {item.icon}
                <span className="truncate max-w-32">{item.label}</span>
              </button>
            ) : (
              <span className={cn(
                "flex items-center gap-1.5 px-2 py-1",
                item.isActive 
                  ? "text-primary font-medium" 
                  : "text-slate-600"
              )}>
                {item.icon}
                <span className="truncate max-w-32">{item.label}</span>
              </span>
            )}
          </div>
        </React.Fragment>
      ))}
    </nav>
  );
};

// Helper function to create breadcrumb items
export const createProjectBreadcrumb = (
  projectName: string, 
  folderName?: string, 
  onNavigateToProject?: () => void,
  onNavigateToFolder?: () => void
): BreadcrumbItem[] => {
  const items: BreadcrumbItem[] = [
    {
      label: "Uppdrag",
      icon: <Home className="h-4 w-4" />,
    },
    {
      label: projectName,
      icon: <Building2 className="h-4 w-4" />,
      onClick: onNavigateToProject,
      isActive: !folderName
    }
  ];

  if (folderName) {
    items.push({
      label: folderName,
      icon: <Folder className="h-4 w-4" />,
      onClick: onNavigateToFolder,
      isActive: true
    });
  }

  return items;
};